import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SectionGlow from '@/components/common/SectionGlow';
import { useLanguage } from '@/contexts/LanguageContext';
import { RTL_LANGS } from '@/i18n';
import { TipWidget } from '@/components/tips/TipWidget';
import { JoinCta, useIsMember } from '@/components/common/JoinCta';
import { Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BodyText = ({ html, rtl }) => (
  <div
    className="ah-body text-body-lg"
    style={{ color: 'var(--text-secondary)' }}
    dir="ltr"
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

const BlockImage = ({ src, className = '', style, natural = false }) => (
  <div className={`gold-tier2 relative w-full overflow-hidden rounded-3xl ${className}`} style={style}>
    <img src={src} alt="SAMTWO" className={`w-full ${natural ? 'h-auto' : 'h-full object-cover'}`} />
  </div>
);

// Full-width aspect ratio: default 16/9 when unset; "auto" = natural image height.
const RATIO = { '16:9': '16 / 9', '4:3': '4 / 3', '3:2': '3 / 2', '1:1': '1 / 1' };
const fullWidthAspect = (r) => (r === 'auto' ? undefined : (RATIO[r] || '16 / 9'));
// Side (left/right) column aspect: default 4/5 when unset so existing blocks
// (which stored null) keep their current portrait render; "auto" = natural.
const SIDE_RATIO = { '4:5': '4 / 5', '3:4': '3 / 4', '9:16': '9 / 16', '1:1': '1 / 1' };
const sideAspect = (r) => (r === 'auto' ? undefined : (SIDE_RATIO[r] || '4 / 5'));

const Block = ({ block, rtl }) => {
  const hasImage = !!block.image_url;
  const align = block.image_alignment;

  // No image, or explicit full-width: text spans full width (image on top when present).
  if (!hasImage) {
    return (
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        data-testid={`story-block-${block.id}`}>
        <BodyText html={block.body} rtl={rtl} />
      </motion.div>
    );
  }

  if (align === 'full_width') {
    return (
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        data-testid={`story-block-${block.id}`}>
        <BlockImage src={block.image_url} style={{ aspectRatio: fullWidthAspect(block.image_aspect_ratio) }} natural={block.image_aspect_ratio === 'auto'} />
        <div className="mt-8">
          <BodyText html={block.body} rtl={rtl} />
        </div>
      </motion.div>
    );
  }

  // left = image left / text right; right = text left / image right.
  // Mobile always stacks the image above the text (flex-col, image first in DOM).
  const rowDir = align === 'right' ? 'lg:flex-row-reverse' : 'lg:flex-row';
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
      className={`flex flex-col ${rowDir} gap-8 lg:gap-14 lg:items-start`} data-testid={`story-block-${block.id}`}>
      <div className="w-full lg:w-1/2">
        <BlockImage src={block.image_url} className="max-w-md mx-auto" style={{ aspectRatio: sideAspect(block.image_aspect_ratio) }} natural={block.image_aspect_ratio === 'auto'} />
      </div>
      <div className="w-full lg:w-1/2">
        <BodyText html={block.body} rtl={rtl} />
      </div>
    </motion.div>
  );
};

const ArtistStory = () => {
  const { t, lang } = useLanguage();
  const isMember = useIsMember();
  const rtl = RTL_LANGS.includes(lang);
  const [blocks, setBlocks] = useState([]);
  const [tipHeader, setTipHeader] = useState('Tip this poor guy');

  useEffect(() => {
    axios.get(`${API}/home/artist-history`, { params: { lang } })
      .then((r) => setBlocks(r.data?.blocks || [])).catch(() => setBlocks([]));
  }, [lang]);

  useEffect(() => {
    axios.get(`${API}/home/tip-widget`, { params: { lang } }).then((r) => { if (r.data?.header_text) setTipHeader(r.data.header_text); }).catch(() => {});
  }, [lang]);

  if (!blocks.length) return null;

  return (
    <section data-testid="artist-story-section" className="relative py-24 md:py-36 overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
      <SectionGlow position="70% 30%" strength={9} />
      <style>{`.ah-body p{margin:0 0 1.1em;line-height:1.75}.ah-body p:last-child{margin-bottom:0}`}</style>
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, var(--accent-primary) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <span className="text-overline">{t('story.label')}</span>
        </motion.div>

        <div className="mt-12 space-y-16 md:space-y-24">
          {blocks.map((b) => <Block key={b.id} block={b} rtl={rtl} />)}
        </div>

        {/* Closing "Tip this poor guy" pill: opens the tip modal inline, same
            compact-trigger pattern as the Hero CTA. */}
        <div className="mt-16 md:mt-20 flex justify-center">
          {isMember ? (
          <TipWidget
            sourceType="homepage"
            compact
            trigger={({ onClick }) => (
              <button
                data-testid="story-tip-cta"
                onClick={onClick}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold metallic-sheen"
                style={{ color: '#fff' }}
              >
                <Heart size={15} /> {t('home.story.send_tip')}
              </button>
            )}
          />
          ) : (
            <JoinCta source="artist_story" />
          )}
        </div>
      </div>
    </section>
  );
};

export default ArtistStory;
